import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Modal,
  InputGroup,
  Spinner,
} from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { FaEdit, FaTrash, FaFileExcel } from 'react-icons/fa';
import axios from 'axios';
import BASE_URL from '../config';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './AdminLayout.css';
import MapPopup from '../components/Map';

const defaultCenter = [24.4667, 54.3667];

const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  const pad = (n) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const AdminCustomers = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    latitude: '',
    longitude: '',
    externalId: '',
    address: '',
  });
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showMapPopup, setShowMapPopup] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [messageModal, setMessageModal] = useState({
    show: false,
    title: '',
    body: '',
  });
  const [isExporting, setIsExporting] = useState(false);
  const [mapLanguage, setMapLanguage] = useState('en');

  // Infinite scroll state variables
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const isFetchingRef = useRef(false);

  // Keep fetchAllCustomers for export functionality
  const fetchAllCustomers = async () => {
    let list = [];
    let page = 1;
    let hasMore = true;
    try {
      while (hasMore) {
        const res = await authAxios.get(`/customers?page=${page}&limit=100`);
        const apiData = res.data?.data;
        const pageCustomers = apiData?.data || res.data;
        if (Array.isArray(pageCustomers) && pageCustomers.length > 0) {
          list = [...list, ...pageCustomers];
          if (
            apiData?.current_page &&
            apiData?.last_page &&
            apiData.current_page < apiData.last_page
          ) {
            page++;
          } else {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      return list;
    } catch (error) {
      console.error('Error fetching all customers:', error);
      throw new Error('Failed to fetch all customer data.');
    }
  };

  // Fetch single page of customers
  const fetchCustomersPage = async (page, rowsPerPage) => {
    try {
      const res = await authAxios.get(
        `/customers?page=${page}&per_page=${rowsPerPage}`
      );
      const responseData = res.data?.data || res.data;
      const customersData = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
        ? responseData
        : [];

      // Get pagination metadata
      const lastPage =
        responseData?.last_page ??
        responseData?.meta?.last_page ??
        res.data?.last_page;
      const currentPageNum =
        responseData?.current_page ?? responseData?.meta?.current_page ?? page;
      const total =
        responseData?.total ?? responseData?.meta?.total ?? res.data?.total;

      return {
        customersData,
        lastPage,
        currentPageNum,
        total,
        hasMore: lastPage
          ? currentPageNum < lastPage
          : customersData.length === rowsPerPage,
      };
    } catch (err) {
      console.error(`Error fetching customers page ${page}:`, err);
      throw err;
    }
  };

  // Fetch all customers page by page until last page
  const fetchAllCustomersSequentially = async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setLoading(true);
    setAllCustomers([]);
    setCurrentPage(1);
    setHasMore(true);

    try {
      let page = 1;
      let allCustomersList = [];
      let lastPageNum = null;
      let totalCount = 0;

      // Fetch first page to get total and last page info
      const firstPageData = await fetchCustomersPage(page, perPage);
      allCustomersList = [...firstPageData.customersData];
      lastPageNum = firstPageData.lastPage;
      totalCount = firstPageData.total || 0;

      // Update state with first page data
      setAllCustomers(allCustomersList);
      setCurrentPage(page);
      setLastPage(lastPageNum || 1);
      setTotalRows(totalCount || allCustomersList.length);
      setIsLoadingMore(true);

      // If there are more pages, fetch them sequentially
      if (firstPageData.hasMore && lastPageNum) {
        for (page = 2; page <= lastPageNum; page++) {
          try {
            const pageData = await fetchCustomersPage(page, perPage);
            allCustomersList = [...allCustomersList, ...pageData.customersData];

            // Update state progressively as each page is fetched
            setAllCustomers([...allCustomersList]);
            setCurrentPage(page);

            console.log(
              `Fetched page ${page} of ${lastPageNum}, total customers: ${allCustomersList.length}`
            );
          } catch (err) {
            console.error(`Error fetching page ${page}:`, err);
            // Continue with next page even if one fails
          }
        }
      } else if (firstPageData.hasMore && !lastPageNum) {
        // If we don't have last_page info, continue fetching until we get an empty page
        page = 2;
        while (true) {
          try {
            const pageData = await fetchCustomersPage(page, perPage);
            if (pageData.customersData.length === 0) {
              // Empty page means we've reached the end
              break;
            }
            allCustomersList = [...allCustomersList, ...pageData.customersData];
            setAllCustomers([...allCustomersList]);
            setCurrentPage(page);

            // If we got fewer items than per_page, it's likely the last page
            if (pageData.customersData.length < perPage) {
              break;
            }
            page++;
          } catch (err) {
            console.error(`Error fetching page ${page}:`, err);
            break;
          }
        }
      }

      setHasMore(false);
      setLastPage(page);
      console.log(
        `Finished fetching all customers. Total: ${allCustomersList.length}`
      );
    } catch (err) {
      console.error('Error fetching all customers:', err);
      setAllCustomers([]);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      isFetchingRef.current = false;
    }
  };

  const refreshCustomers = () => {
    fetchAllCustomersSequentially();
  };

  useEffect(() => {
    refreshCustomers();
  }, []);

  const filteredCustomers = useMemo(() => {
    const lowerCaseSearch = search.toLowerCase();
    if (!lowerCaseSearch) {
      return allCustomers;
    }
    return allCustomers.filter(
      (c) =>
        c.name?.toLowerCase().includes(lowerCaseSearch) ||
        c.email?.toLowerCase().includes(lowerCaseSearch) ||
        c.phone?.toLowerCase().includes(lowerCaseSearch) ||
        c.address?.toLowerCase().includes(lowerCaseSearch)
    );
  }, [allCustomers, search]);

  // Removed Intersection Observer - now fetching all pages sequentially

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      const exportList = await fetchAllCustomers();
      if (!exportList || exportList.length === 0) {
        setMessageModal({
          show: true,
          title: 'Error',
          body: 'No customer data available to export!',
        });
        setIsExporting(false);
        return;
      }
      const exportData = exportList.map((c, index) => ({
        'S.No': index + 1,
        Name: c.name,
        Email: c.email,
        Phone: c.phone,
        Address: c.address,
        Location:
          c.latitude || (c.lat && c.longitude) || c.lng
            ? `${c.latitude || c.lat}, ${c.longitude || c.lng}`
            : '',
        'External ID': c.external_id,
        Created: formatTimestamp(c.created_at),
        Updated: formatTimestamp(c.updated_at),
      }));
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      const data = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });
      saveAs(data, `customers_${new Date().toISOString().slice(0, 10)}.xlsx`);
      setMessageModal({
        show: true,
        title: 'Success',
        body: `${exportList.length} customers exported successfully!`,
      });
    } catch (error) {
      setMessageModal({
        show: true,
        title: 'Export Error',
        body: error.message || 'An unknown error occurred during export.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      lat: formData.latitude ? parseFloat(formData.latitude) : null,
      lng: formData.longitude ? parseFloat(formData.longitude) : null,
      external_id: formData.externalId,
    };
    try {
      await authAxios.post('/customers', payload);
      setMessageModal({
        show: true,
        title: 'Success',
        body: 'Customer created successfully!',
      });
      refreshCustomers();
      setFormData({
        name: '',
        phone: '',
        email: '',
        latitude: '',
        longitude: '',
        externalId: '',
        address: '',
      });
      setShowNewCustomer(false);
    } catch (error) {
      console.error('Creation Error:', error.response?.data || error);
      setMessageModal({
        show: true,
        title: 'Error',
        body: error.response?.data?.message || 'Failed to create customer.',
      });
    }
  };

  const handleEditClick = (customer) => {
    setCurrentCustomer({
      ...customer,
      latitude: customer.lat || customer.latitude || '',
      longitude: customer.lng || customer.longitude || '',
      externalId: customer.external_id || '',
    });
    setShowEditModal(true);
  };
  const handleEditChange = (e) =>
    setCurrentCustomer((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      await authAxios.put(`/customers/${currentCustomer.id}`, {
        ...currentCustomer,
        lat: currentCustomer.latitude
          ? parseFloat(currentCustomer.latitude)
          : null,
        lng: currentCustomer.longitude
          ? parseFloat(currentCustomer.longitude)
          : null,
        external_id: currentCustomer.externalId,
      });
      setMessageModal({
        show: true,
        title: 'Success',
        body: 'Customer updated successfully!',
      });
      setShowEditModal(false);
      refreshCustomers();
    } catch (error) {
      console.error('Update Error:', error.response?.data || error);
      setMessageModal({
        show: true,
        title: 'Error',
        body: error.response?.data?.message || 'Failed to update customer.',
      });
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await authAxios.delete(`/customers/${id}`);
        setMessageModal({
          show: true,
          title: 'Success',
          body: 'Customer deleted successfully!',
        });
        refreshCustomers();
      } catch (error) {
        console.error('Deletion Error:', error.response?.data || error);
        setMessageModal({
          show: true,
          title: 'Error',
          body: error.response?.data?.message || 'Failed to delete customer.',
        });
      }
    }
  };

  const handleSelectLocation = (coords, address) => {
    const [lat, lng] = [parseFloat(coords[0]), parseFloat(coords[1])];
    if (showNewCustomer) {
      setFormData({
        ...formData,
        latitude: lat.toFixed(6).toString(),
        longitude: lng.toFixed(6).toString(),
        address,
      });
    } else if (showEditModal && currentCustomer) {
      setCurrentCustomer((prev) => ({
        ...prev,
        latitude: lat.toFixed(6).toString(),
        longitude: lng.toFixed(6).toString(),
        address,
      }));
    }
    setShowMapPopup(false);
  };

  const openCreateMap = () => {
    if (formData.latitude && formData.longitude) {
      setMapCenter([
        parseFloat(formData.latitude),
        parseFloat(formData.longitude),
      ]);
    } else {
      setMapCenter(defaultCenter);
    }
    setShowMapPopup(true);
  };

  const openEditMap = () => {
    if (
      currentCustomer &&
      currentCustomer.latitude &&
      currentCustomer.longitude
    ) {
      setMapCenter([
        parseFloat(currentCustomer.latitude),
        parseFloat(currentCustomer.longitude),
      ]);
    } else {
      setMapCenter(defaultCenter);
    }
    setShowMapPopup(true);
  };

  const columns = [
    {
      name: 'S.No',
      selector: (row, index) => index + 1,
      sortable: true,
      width: '3vw',
      compact: true,
      grow: 0,
    },
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '8vw',
      wrap: true,
      grow: 1,
      cell: (row) => (
        <div style={{ wordBreak: 'break-word' }}>{row.name || 'N/A'}</div>
      ),
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      minWidth: '10vw',
      wrap: true,
      grow: 1,
      cell: (row) => (
        <div style={{ wordBreak: 'break-word' }}>{row.email || 'N/A'}</div>
      ),
    },
    {
      name: 'Phone',
      selector: (row) => row.phone,
      sortable: true,
      minWidth: '6vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Address',
      selector: (row) => row.address,
      sortable: true,
      minWidth: '12vw',
      wrap: true,
      grow: 2,
      cell: (row) => (
        <div style={{ wordBreak: 'break-word' }}>{row.address || 'N/A'}</div>
      ),
    },
    {
      name: 'Created',
      selector: (row) => formatTimestamp(row.created_at),
      sortable: true,
      minWidth: '7.5vw',
      wrap: true,
      grow: 1,
      cell: (row) => <small>{formatTimestamp(row.created_at)}</small>,
    },
    {
      name: 'Actions',
      center: true,
      cell: (row) => {
        const buttons = [
          <Button
            key='edit'
            size='sm'
            variant='warning'
            onClick={() => handleEditClick(row)}
          >
            <FaEdit />
          </Button>,
          <Button
            key='delete'
            size='sm'
            variant='danger'
            onClick={() => handleDeleteCustomer(row.id)}
          >
            <FaTrash />
          </Button>,
        ];

        return (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            {buttons.map((button) =>
              React.cloneElement(button, {
                style: {
                  ...button.props.style,
                  marginTop: '4px',
                  marginBottom: '4px',
                },
              })
            )}
          </div>
        );
      },
      minWidth: '6vw',
      grow: 0,
    },
  ];

  // Custom Loading Component for DataTable
  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>Loading Customers...</span>
    </div>
  );

  // Custom No Data Component for DataTable
  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <h5 className='mt-2'>No Customers Found</h5>
      <p className='mb-0'>
        There are currently no customers in this list or matching your search
        criteria.
      </p>
    </div>
  );

  return (
    <div className='border p-2 rounded' style={{ background: '#fff' }}>
      <Container fluid className='p-4'>
        <h3 style={{ textAlign: 'center' }}>Customer Management</h3>

        <div className='row justify-content-between align-items-center mt-3 mb-3'>
          <div className='col-md-9 col-lg-9 col-sm-12 align-items-center py-3 gap-2'>
            <Row className='g-2'>
              <Col xs='auto'>
                <InputGroup style={{ width: '300px' }}>
                  <InputGroup.Text>
                    <i className='fa fa-search'></i>
                  </InputGroup.Text>
                  <Form.Control
                    type='text'
                    placeholder='Search Customer'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </div>
          <div className='col-md-3 col-lg-3 col-sm-12 text-end'>
            <Button
              size='sm'
              variant='primary'
              onClick={() => setShowNewCustomer(true)}
              style={{ marginRight: '2px' }}
            >
              <i className='fa fa-plus'></i> New Customer
            </Button>
            <Button
              size='sm'
              variant='success'
              onClick={handleExportToExcel}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                    aria-hidden='true'
                  ></span>
                  Exporting...
                </>
              ) : (
                <>
                  <FaFileExcel /> Export to Excel
                </>
              )}
            </Button>
          </div>
        </div>

        <div style={{ maxHeight: '58vh' }}>
          {/* Count Display */}
          {filteredCustomers.length > 0 && (
            <div
              className='text-end p-2'
              style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
              }}
            >
              <small className='text-muted'>
                Showing <strong>{1}</strong> to{' '}
                <strong>{filteredCustomers.length}</strong> out of{' '}
                <strong>{totalRows}</strong> customers
              </small>
            </div>
          )}
          <DataTable
            columns={columns}
            data={filteredCustomers}
            pagination={false}
            highlightOnHover
            striped
            dense
            persistTableHead
            fixedHeader
            fixedHeaderScrollHeight='45vh'
            customStyles={{
              table: {
                style: {
                  tableLayout: 'auto',
                  width: '100%',
                  maxWidth: '100%',
                },
              },
              tableWrapper: {
                style: {
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                },
              },
              headRow: {
                style: {
                  backgroundColor: '#f8f9fa',
                  borderBottomStyle: 'solid',
                  borderBottomWidth: '2px',
                  borderBottomColor: '#dee2e6',
                },
              },
              headCells: {
                style: {
                  fontSize: '14px',
                  fontWeight: '600',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                },
              },
              cells: {
                style: {
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                },
              },
            }}
            progressPending={loading && !isLoadingMore}
            progressComponent={CustomLoadingIndicator}
            noDataComponent={CustomNoDataComponent}
          />
          {/* Infinite scroll loading indicator */}
          
          {!loading && !isLoadingMore && filteredCustomers.length > 0 && (
            <div className='text-center p-3 text-muted'>
              <small>
                All customers loaded ({filteredCustomers.length} total)
              </small>
            </div>
          )}
          {isLoadingMore && (
            <div className='text-center p-3'>
              <Spinner animation='border' size='sm' className='me-2' />
              <small className='text-muted'>
                Loading customers... (Page {currentPage}
                {lastPage > 1 ? ` of ${lastPage}` : ''})
              </small>
            </div>
          )}
        </div>

        <Modal
          show={messageModal.show}
          onHide={() => setMessageModal({ show: false, title: '', body: '' })}
          centered
          size='md'
          className='text-center'
        >
          <Modal.Body
            className='py-4'
            style={{
              backgroundColor: '#fff',
              color: '#1f1f1f',
              borderRadius: '15px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <div className='mb-3'>
              {messageModal.title === 'Success' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='green'
                  className='bi bi-check-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.08 0l3.992-3.992a.75.75 0 0 0-1.08-1.04L7.5 9.439 5.53 7.47a.75.75 0 0 0-1.08 1.04l2.52 2.52z' />
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='red'
                  className='bi bi-x-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM4.646 4.646a.5.5 0 0 0 0 .708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646a.5.5 0 0 0-.708 0z' />
                </svg>
              )}
            </div>
            <h5 className='mb-2'>{messageModal.title}</h5>
            <p>{messageModal.body}</p>
            <Button
              variant={messageModal.title === 'Success' ? 'success' : 'danger'}
              style={{ width: '100%' }}
              onClick={() =>
                setMessageModal({ show: false, title: '', body: '' })
              }
            >
              Close
            </Button>
          </Modal.Body>
        </Modal>

        <MapPopup
          show={showMapPopup}
          handleClose={() => setShowMapPopup(false)}
          title='Select Customer Location'
          center={mapCenter}
          setCoordinatesAndAddress={handleSelectLocation}
          mapLanguage={mapLanguage}
        />

        <Modal
          show={showNewCustomer}
          onHide={() => setShowNewCustomer(false)}
          size='lg'
        >
          <Modal.Header closeButton>
            <Modal.Title>Create Customer</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      required
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className='mb-2'>
                    <Form.Label>External ID</Form.Label>
                    <Form.Control
                      value={formData.externalId}
                      onChange={(e) =>
                        setFormData({ ...formData, externalId: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className='align-items-center mb-2'>
                <Col md={4}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control
                      name='latitude'
                      value={formData.latitude}
                      onChange={(e) =>
                        setFormData({ ...formData, latitude: e.target.value })
                      }
                      type='number'
                      step='any'
                      placeholder='Enter latitude'
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control
                      name='longitude'
                      value={formData.longitude}
                      onChange={(e) =>
                        setFormData({ ...formData, longitude: e.target.value })
                      }
                      type='number'
                      step='any'
                      placeholder='Enter longitude'
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className='mt-4'>
                  <Button
                    className='w-100'
                    variant='success'
                    onClick={openCreateMap}
                  >
                    📍 Select on Map
                  </Button>
                </Col>
              </Row>

              <Form.Group className='mb-2'>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                />
              </Form.Group>
              <Button type='submit' className='w-100 mt-2' variant='success'>
                + Create Customer
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size='lg'
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Customer</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentCustomer && (
              <Form onSubmit={handleUpdateCustomer}>
                <Row>
                  <Col md={6}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        name='name'
                        value={currentCustomer.name}
                        onChange={handleEditChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        name='phone'
                        value={currentCustomer.phone}
                        onChange={handleEditChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        name='email'
                        value={currentCustomer.email}
                        onChange={handleEditChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className='mb-2'>
                      <Form.Label>External ID</Form.Label>
                      <Form.Control
                        name='externalId'
                        value={
                          currentCustomer.externalId ||
                          currentCustomer.external_id ||
                          ''
                        }
                        onChange={handleEditChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='align-items-center mb-2'>
                  <Col md={4}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Latitude</Form.Label>
                      <Form.Control
                        name='latitude'
                        value={currentCustomer.latitude}
                        onChange={handleEditChange}
                        type='number'
                        step='any'
                        placeholder='Enter latitude'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className='mb-2'>
                      <Form.Label>Longitude</Form.Label>
                      <Form.Control
                        name='longitude'
                        value={currentCustomer.longitude}
                        onChange={handleEditChange}
                        type='number'
                        step='any'
                        placeholder='Enter longitude'
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className='mt-4'>
                    <Button
                      className='w-100'
                      variant='success'
                      onClick={openEditMap}
                    >
                      📍 Select on Map
                    </Button>
                  </Col>
                </Row>

                <Form.Group className='mb-2'>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    name='address'
                    value={currentCustomer.address}
                    onChange={handleEditChange}
                  />
                </Form.Group>
                <Button type='submit' className='w-100 mt-2' variant='success'>
                  Update Customer
                </Button>
              </Form>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default AdminCustomers;
